/**
 * @module NotEstablishedPanel
 * @purpose L3 panel — renders Part C not_established[] as "What the evidence does not establish"
 *   in muted/cautionary styling beside the provenance chain.
 * @depends types/reasoning, react-i18next
 * @dependedOnBy FactPackPanel (L2 drill-down), ReasoningClusterCard L3 region
 *
 * @rules
 * - Performance: pure display — no data fetching; renders nothing on empty array
 * - State: none — stateless presentational component
 * - Errors: n/a — purely presentational; empty array renders nothing
 */

import { useTranslation } from 'react-i18next'

interface NotEstablishedPanelProps {
  notEstablished: string[]
}

const PANEL_WRAPPER: React.CSSProperties = {
  marginTop: '10px',
  padding: '8px 12px',
  borderRadius: '5px',
  border: '1px solid var(--border)',
  background: 'var(--bg)',
}

const HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.74rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: '6px',
}

const ITEM: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.76rem',
  color: 'var(--text-dim)',
  marginBottom: '3px',
  paddingLeft: '4px',
}

const EMPTY_DASH: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.76rem',
  color: 'var(--text-muted)',
}

/**
 * Renders the "not established" panel showing what evidence does NOT prove.
 *
 * @behaviour Empty array → renders nothing (returns empty fragment).
 *   Non-empty array → renders a muted/cautionary panel with a bulleted list.
 *   Pure prop-driven; no hooks other than useTranslation.
 * @errors n/a — purely presentational
 */
export function NotEstablishedPanel({ notEstablished }: NotEstablishedPanelProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  if (notEstablished.length === 0) return <></>

  return (
    <div style={PANEL_WRAPPER} role="note" aria-label={t('reasoning.briefingBoard.notEstablished.heading' as never)}>
      <div style={HEADING}>
        {t('reasoning.briefingBoard.notEstablished.heading' as never)}
      </div>
      <ul style={{ listStyle: 'disc', paddingLeft: '16px', margin: 0 }}>
        {notEstablished.map((item) => (
          <li key={item} style={ITEM}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Renders a neutral dash when the not_established list is empty.
 *
 * @behaviour Shows "—" with a muted i18n label when the caller explicitly wants
 *   to show an empty state rather than nothing. Use NotEstablishedPanel for the
 *   standard case (renders nothing when empty).
 * @errors n/a — purely presentational
 */
export function NotEstablishedEmpty(): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <span style={EMPTY_DASH}>
      {t('reasoning.briefingBoard.notEstablished.empty' as never)}
    </span>
  )
}
